<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  userLocation: {
    type: String,
    default: 'London'
  }
});

const newsItems = ref([]);
const loading = ref(false);
const errorMessage = ref('');

// Helper to clean up HTML entities in titles
const decodeEntities = (html) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

const fetchLocalNews = async () => {
  if (!props.userLocation) return;

  loading.value = true;
  errorMessage.value = '';

  try {
    // Call your internal Vercel serverless route instead of rss2json directly
    const apiBase = process.env.VUE_APP_API_BASE_URL || '';
    const response = await fetch(`${apiBase}/api/news?location=${encodeURIComponent(props.userLocation)}`);
    const data = await response.json();

    if (!response.ok || data.status !== 'ok' || !data.items) {
      throw new Error('Unable to load news headlines');
    }

    // Take top 5 articles
    newsItems.value = data.items.slice(0, 5).map(item => ({
      title: decodeEntities(item.title),
      link: item.link,
      pubDate: new Date(item.pubDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    }));
  } catch (err) {
    newsItems.value = [];
    errorMessage.value = 'Failed to load local news';
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.userLocation,
  () => fetchLocalNews(),
  { immediate: true }
);
</script>

<template>
  <div class="news-card">
    <!-- Header Section -->
    <div class="news-header">
      <i class="bi bi-newspaper news-icon"></i>
      <h2 class="location">Local News</h2>
    </div>

    <!-- Loading & Error States -->
    <div v-if="loading" class="state-message">Fetching latest news...</div>
    <div v-else-if="errorMessage" class="error-message">{{ errorMessage }}</div>

    <!-- News List -->
    <div v-else-if="newsItems.length" class="news-list">
      <a 
        v-for="(item, index) in newsItems" 
        :key="index"
        :href="item.link" 
        target="_blank" 
        rel="noopener noreferrer" 
        class="news-item"
      >
        <p class="news-title">{{ item.title }}</p>
        <div class="news-meta">
          <span class="source-tag">BBC News</span>
          <span class="news-time">{{ item.pubDate }}</span>
        </div>
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/abstract/variables" as *;

.news-card {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  font-family: system-ui, -apple-system, sans-serif;

  .news-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin-bottom: 0.5rem;

    .news-icon {
      font-size: 1.15rem;
      color: #d97706;
    }

    .location {
      font-size: 1.15rem;
      color: #d97706;
      margin: 0;
      font-weight: 700;
    }
  }

  .state-message, .error-message { 
    margin: auto 0; 
    font-size: 0.95rem; 
  }
  .state-message { color: #64748b; }
  .error-message { color: #e11d48; }

  .news-list {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;

    .news-item {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      text-align: left;
      background: #f8fafc;
      padding: 9px 12px;
      border-radius: 10px;
      border: 1px solid #e2e8f0;
      text-decoration: none;
      transition: all 0.2s ease;

      &:hover {
        border-color: #cbd5e1;
        background: #ffffff;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);

        .news-title {
          color: #2563eb;
        }
      }

      .news-title {
        font-size: 0.8rem;
        font-weight: 600;
        color: #0f172a;
        margin: 0 0 6px 0;
        line-height: 1.3;

        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        transition: color 0.15s ease;
      }

      .news-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .source-tag {
          font-size: 0.65rem;
          font-weight: 700;
          color: #b91c1c;
          background-color: #fef2f2;
          padding: 2px 7px;
          border-radius: 6px;
          border: 1px solid #fee2e2;
        }

        .news-time {
          font-size: 0.7rem;
          color: #64748b;
          font-weight: 500;
        }
      }
    }
  }
}
</style>